import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { isLocalDev } from '../lib/isLocalDev';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

function readSiteKey() {
  if (typeof window !== 'undefined' && window.__TURNSTILE_SITE_KEY__) {
    return window.__TURNSTILE_SITE_KEY__;
  }
  return process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
}

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile script failed'));
    document.head.appendChild(script);
  });
}

const TurnstileField = forwardRef(function TurnstileField({ onToken, onExpire, onReady }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onReadyRef = useRef(onReady);
  const [siteKey, setSiteKey] = useState('');
  const [status, setStatus] = useState(() => (isLocalDev() ? 'unavailable' : 'loading'));

  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
    onReadyRef.current = onReady;
  });

  useEffect(() => {
    if (isLocalDev()) {
      onReadyRef.current?.(false);
      return undefined;
    }

    let cancelled = false;

    const resolveKey = async () => readSiteKey();

    resolveKey().then((key) => {
      if (cancelled) return;
      if (!key) {
        setStatus('unavailable');
        onReadyRef.current?.(false);
        return;
      }
      setSiteKey(key);
      setStatus('loading');
      onReadyRef.current?.(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const mountWidget = useCallback(async () => {
    if (!siteKey || !containerRef.current || widgetIdRef.current != null) return;

    try {
      await loadTurnstileScript();
      if (!containerRef.current || widgetIdRef.current != null || !window.turnstile) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'auto',
        appearance: 'always',
        callback: (token) => {
          onTokenRef.current?.(token);
          setStatus('ready');
        },
        'expired-callback': () => {
          onTokenRef.current?.('');
          setStatus('expired');
          onExpireRef.current?.();
        },
        'error-callback': () => {
          onTokenRef.current?.('');
          setStatus('error');
        },
      });
    } catch {
      setStatus('error');
      onReadyRef.current?.(false);
    }
  }, [siteKey]);

  const containerCallbackRef = useCallback(
    (node) => {
      containerRef.current = node;
      if (node && siteKey) {
        mountWidget();
      }
    },
    [siteKey, mountWidget],
  );

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      onTokenRef.current?.('');
    },
  }));

  useEffect(() => {
    if (!siteKey) return undefined;

    mountWidget();

    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, mountWidget]);

  if (isLocalDev() || status === 'unavailable') return null;

  return (
    <div
      className={`cta-form-turnstile${status === 'ready' ? ' is-ready' : ''}${status === 'error' ? ' is-error' : ''}`}
      ref={containerCallbackRef}
      aria-live="polite"
      data-turnstile-status={status}
    />
  );
});

export default TurnstileField;
